import { inject, Injectable } from '@angular/core';
import { first, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import * as uuid from 'uuid';
import { SOCKET_ACCOUNT_EVENT } from '../../enums/events-buffer/socket/socket-account-event.enum';
import { IAudit, IAuditBody } from '../../interfaces/audit.interface';
import { IDevice } from '../device/models/device.model';
import { IOrganization } from '../organization/domain/interfaces/organization.interface';
import { AuditService } from '../audit/audit.service';
import { DeviceService } from '../device/device.service';
import { OrganizationRepositoryAdapter } from '../organization/infrastructure/adapters/organization-repository.adapter';
import { Account } from './domain/model/account.model';
import { AccountApiMapper } from './infrastructure/mappers/account-api.mapper';

import { SERVICE_STATE_SERVICE } from '@features/service/domain/tokens/service-state.token';
import { Worker } from '@features/service/domain/models/worker.model';

@Injectable({
  providedIn: 'root'
})
export class AccountAuditHandlerService {
  private readonly organizationService = inject(OrganizationRepositoryAdapter);
  private readonly auditService = inject(AuditService);
  private readonly deviceService = inject(DeviceService);

  private readonly serviceStateService = inject(SERVICE_STATE_SERVICE);

  auditCreateAccountEvent(newAccount: Account): Observable<IAudit | null> {
    return this.getAuditBodyData().pipe(
      first(),
      switchMap((auditBodyData) => {
        const auditBody: IAuditBody = {
          _id: uuid.v4(),
          deviceId: auditBodyData?.device?._id ?? '',
          action: SOCKET_ACCOUNT_EVENT.create,
          authorId: auditBodyData.currentWorker?.id ?? '',
          timestamp: Date.now(),
          payload: {...AccountApiMapper.toApi(newAccount)},
        };

        return this.auditService.insertAuditBodyInCurrentAuditAccount(auditBody);
      })
    );
  }

  private getAuditBodyData(): Observable<{
    organization: IOrganization | null;
    currentWorker: Worker | null;
    currentAudit: IAudit | null;
    device: IDevice | null;
  }> {
    return forkJoin({
      organization: this.organizationService.getCurrentOrganization(),
      currentWorker: of(this.serviceStateService.getCurrentWorker()),
      currentAudit: this.auditService.getCurrentAuditAccount(),
      device: this.deviceService.getDevice(),
    }).pipe(
      first(),
      map(({ organization, currentWorker, currentAudit, device }) => {
        return {
          organization,
          currentWorker,
          currentAudit,
          device,
        };
      })
    );
  }

}
